import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  getDialCreditBalance,
  getDialCreditConfig,
  getDialCreditTransactions,
  createDialCreditCheckout,
  updateDialCreditSettings,
  type DialCreditTxnType,
} from "../services/dialCreditsService";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import { removeRealtimeChannel } from "../../../../_module/services/realtimeTeardown";
import { getSupabaseClient } from "@/app/_module/services/supabaseClient";
import { useRealtimeRejoin } from "@/app/_module/services/realtimeHealth";

export const DIAL_CREDIT_KEYS = {
  balance: ["dial-credits", "balance"] as const,
  config: ["dial-credits", "config"] as const,
  transactions: (filters?: { type?: DialCreditTxnType; limit?: number; offset?: number }) =>
    ["dial-credits", "transactions", filters] as const,
};

export function useDialCreditBalance(opts?: { enabled?: boolean }) {
  const qc = useQueryClient();
  const { user } = useUserContext();
  const userId = (user as any)?.user?.id || (user as any)?.id;
  const enabled = opts?.enabled ?? true;

  const query = useQuery({
    queryKey: DIAL_CREDIT_KEYS.balance,
    queryFn: getDialCreditBalance,
    enabled,
    // Polling kept as a safety net — realtime push handles the instant case.
    // If the websocket drops, the user still gets fresh data within 30s.
    refetchInterval: 30_000,
    staleTime: 15_000,
    retry: 1,
  });

  // Guarded rejoin: exponential backoff + a hard attempt cap. Without the cap a
  // permanently failing channel re-subscribes in a tight loop and hangs the tab.
  const { rejoinToken, handleStatus } = useRealtimeRejoin("dial-credits");

  // Real-time push: subscribe to the user's dial-credits channel.
  // On any balance_updated event, immediately refetch the balance.
  useEffect(() => {
    if (!enabled || !userId) return;
    let channel: any = null;
    let cancelled = false;

    // DEAD-CLIENT FIX (2026-08-12): this used to build its own client from
    // NEXT_PUBLIC_SUPABASE_URL / _ANON_KEY. Those vars are UNSET in this app — the real
    // client decodes the obfuscated NEXT_PUBLIC_SB_U / SB_K — so the guard below always
    // returned early and this subscription NEVER connected in production. Dial credits
    // have been running purely on their 30s poll. Use the shared singleton.
    getSupabaseClient()
      .then((supabase) => {
        if (cancelled || !supabase) return;
        channel = supabase.channel(`dial-credits:${userId}`);

        // Server pushes "balance_updated" after every credit/charge/refund
        channel.on(
          "broadcast",
          { event: "balance_updated" },
          (payload: any) => {
            // Optimistic write — we have the new balance from the broadcast,
            // so update the cache immediately. Then refetch to pull the full
            // snapshot (lifetime stats, etc.) authoritatively.
            const data = payload?.payload;
            if (data?.balanceCents !== undefined) {
              qc.setQueryData(DIAL_CREDIT_KEYS.balance, (prev: any) =>
                prev
                  ? {
                      ...prev,
                      balanceCents: data.balanceCents,
                      balanceDollars: data.balanceCents / 100,
                      estimatedMinutesRemaining: prev.costPerMinuteCents
                        ? Math.floor(data.balanceCents / prev.costPerMinuteCents)
                        : prev.estimatedMinutesRemaining,
                      isLowBalance:
                        data.balanceCents < (prev.lowBalanceThresholdCents ?? 500),
                    }
                  : prev,
              );
            }
            // Refetch authoritative snapshot (covers totalUsedCents etc.)
            qc.invalidateQueries({ queryKey: DIAL_CREDIT_KEYS.balance });
            qc.invalidateQueries({ queryKey: ["dial-credits", "transactions"] });
          },
        );

        channel.subscribe(handleStatus);
      })
      .catch(() => {
        // Realtime is best-effort — polling will still keep things fresh
      });

    return () => {
      cancelled = true;
      if (channel) {
        try {
          removeRealtimeChannel(channel);
        } catch {
          /* noop */
        }
      }
    };
  }, [enabled, userId, qc, rejoinToken]);

  return query;
}

export function useDialCreditConfig(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: DIAL_CREDIT_KEYS.config,
    queryFn: getDialCreditConfig,
    enabled: opts?.enabled ?? true,
    staleTime: 5 * 60_000, // config rarely changes
    retry: 1,
  });
}

export function useDialCreditTransactions(
  filters: { type?: DialCreditTxnType; limit?: number; offset?: number } = {},
  opts?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: DIAL_CREDIT_KEYS.transactions(filters),
    queryFn: () => getDialCreditTransactions(filters),
    enabled: opts?.enabled ?? true,
    staleTime: 30_000,
  });
}

export function useCreateDialCreditCheckout() {
  return useMutation({
    mutationFn: (amountCents: number) => createDialCreditCheckout(amountCents),
  });
}

export function useUpdateDialCreditSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateDialCreditSettings,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: DIAL_CREDIT_KEYS.balance });
    },
  });
}
